import React, { useEffect, useState, useCallback } from "react";
import { UserX, Send, Ticket, MessageSquare, Mail, Phone, AlertTriangle, Clock3, ChevronDown, ChevronUp, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { CustomerProfileDrawer } from "@/components/admin/DevPanels";

const fmtDateTime = (iso, lang) =>
  iso ? new Date(iso).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" }) : "—";

const inputCls = "rounded-xl border-brand/20 bg-white h-10 focus-visible:ring-brand";

// Lapsed-customer win-back panel — shared by Dev console and shop dashboard.
export const WinbackPanel = ({ isDev = false }) => {
  const { t, lang } = useLang();
  const a = t.admin;
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [sendOpen, setSendOpen] = useState(false);
  const [channel, setChannel] = useState("sms");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [credits, setCredits] = useState(null);
  const [couponOn, setCouponOn] = useState(false);
  const [coupon, setCoupon] = useState({ kind: "percent", amount: 20, expires: "", service: "both" });
  const [profilePhone, setProfilePhone] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/winback").then((r) => setItems(r.data)).catch(() => toast.error("Error"));
  }, []);
  useEffect(() => {
    load();
    api.get("/admin/templates").then((r) => setMessage(r.data.templates?.sms_winback || "")).catch(() => {});
    if (!isDev) {
      api.get("/admin/sms/balance").then((r) => setCredits(r.data.balance)).catch(() => setCredits(null));
    }
  }, [load, isDev]);

  const toggle = (phone) => setSelected((s) => { const n = new Set(s); n.has(phone) ? n.delete(phone) : n.add(phone); return n; });
  const toggleAll = () => setSelected((s) => (s.size === (items || []).length ? new Set() : new Set(items.map((c) => c.phone))));

  const lowCredits = !isDev && channel === "sms" && credits !== null && credits < 20;

  const openSend = () => {
    if (selected.size === 0) return;
    setSendOpen(true);
  };

  const send = async () => {
    if (!message.trim() || selected.size === 0) return;
    const chLabel = channel === "sms" ? "SMS" : "Email";
    if (!window.confirm(a.winback_confirm.replace("{n}", selected.size).replace("{channel}", chLabel))) return;
    setSending(true);
    try {
      const payload = { recipients: [...selected], channel, message: message.trim() };
      if (couponOn) payload.coupon = coupon;
      const res = await api.post("/admin/winback/send", payload);
      toast.success(a.winback_sent.replace("{s}", res.data.sent).replace("{f}", res.data.failed));
      setSendOpen(false);
      setSelected(new Set());
      setCouponOn(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSending(false);
    }
  };

  if (items === null) return null;

  const previewCustomer = items.find((c) => selected.has(c.phone));
  const previewName = ((previewCustomer?.name || "").split(/\s+/)[0]) || "—";

  const previewCoupon = couponOn && coupon.amount > 0 && coupon.expires
    ? `\nΚουπόνι CFM-XXXX: ${coupon.kind === "percent" ? `-${coupon.amount}%` : `-€${coupon.amount}`} σε ${{ selfwash: "self-service", grooming: "grooming", boutique: "boutique", both: "grooming & self-service" }[coupon.service]} έως ${coupon.expires.split("-").reverse().slice(0, 2).join("/")}.`
    : "";

  return (
    <div className="rounded-2xl bg-amber-50 border border-amber-300 p-5 shadow-sm" data-testid="winback-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-11 w-11 rounded-full bg-amber-100 text-amber-700"><UserX className="h-5 w-5" /></span>
          <div>
            <p className="font-heading text-lg font-bold text-brand-ink" data-testid="winback-count">
              {items.length === 0 ? a.winback_title : a.winback_count.replace("{n}", items.length)}
            </p>
            <p className="text-xs text-brand-ink/60">{a.winback_sub}</p>
          </div>
        </div>
        {items.length > 0 && (
          <Button onClick={() => setOpen(!open)} variant="outline" className="rounded-full border-amber-400 text-amber-800 hover:bg-amber-100 font-bold" data-testid="winback-toggle">
            {open ? <ChevronUp className="h-4 w-4 mr-1.5" /> : <ChevronDown className="h-4 w-4 mr-1.5" />}
            {items.length}
          </Button>
        )}
      </div>

      {items.length === 0 && <p className="mt-3 text-sm text-brand-ink/60 italic" data-testid="winback-empty">{a.winback_empty}</p>}

      {open && items.length > 0 && (
        <div className="mt-4" data-testid="winback-list">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={toggleAll} className="text-xs font-bold text-brand hover:underline" data-testid="winback-select-all">
              {selected.size === items.length ? t.admin.dev.select_none : t.admin.dev.select_all} ({selected.size}/{items.length})
            </button>
            <Button onClick={openSend} disabled={selected.size === 0} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-9" data-testid="winback-send-open">
              <Send className="h-4 w-4 mr-1.5" /> {a.winback_send} ({selected.size})
            </Button>
          </div>
          <div className="space-y-2">
            {items.map((c) => (
              <div key={c.phone} className="rounded-xl bg-white border border-amber-200 px-3 py-2.5" data-testid={`winback-${c.phone}`}>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={selected.has(c.phone)} onChange={() => toggle(c.phone)} className="h-4 w-4 accent-[#864D63] cursor-pointer" data-testid={`winback-check-${c.phone}`} />
                  <button type="button" onClick={() => setProfilePhone(c.phone)} className="min-w-0 flex-1 text-left group" data-testid={`winback-open-${c.phone}`}>
                    <p className="font-bold text-brand-ink text-sm group-hover:text-brand transition-colors">{c.name || "—"}</p>
                    <span className="text-xs text-brand font-semibold inline-flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>
                  </button>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-bold text-amber-700">{a.winback_days_away.replace("{n}", c.days_away)}</p>
                    <p className="text-[11px] text-brand-ink/50 font-mono">{a.winback_last.replace("{d}", c.last_visit)}</p>
                  </div>
                </div>
                {c.sends.length > 0 && (
                  <div className="mt-2 pl-7 space-y-1" data-testid={`winback-history-${c.phone}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-brand-ink/40">{a.winback_history}</p>
                    {c.sends.map((s) => (
                      <p key={s.id} className="text-[11px] text-brand-ink/60 flex items-center gap-1.5 font-mono">
                        {s.channel === "sms" ? <MessageSquare className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                        {fmtDateTime(s.created_at, lang)} · {s.by}
                        {s.coupon_code && <span className="inline-flex items-center gap-0.5 text-emerald-700"><Ticket className="h-3 w-3" />{s.coupon_code}</span>}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {profilePhone && <CustomerProfileDrawer phone={profilePhone} onClose={() => setProfilePhone(null)} isDev={isDev} />}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="bg-brand-cream border-brand/15 max-w-lg rounded-[1.75rem] overflow-y-auto max-h-[92vh]" data-testid="winback-send-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-ink">{a.winback_send} · {selected.size}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-full border border-brand/20 p-1 w-fit">
              <button type="button" onClick={() => setChannel("sms")} className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${channel === "sms" ? "bg-brand text-brand-cream" : "text-brand-ink/60"}`} data-testid="winback-channel-sms">SMS</button>
              <button type="button" onClick={() => setChannel("email")} className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${channel === "email" ? "bg-brand text-brand-cream" : "text-brand-ink/60"}`} data-testid="winback-channel-email">Email</button>
            </div>

            {lowCredits ? (
              <div className="rounded-xl bg-amber-100 border border-amber-300 px-4 py-3 text-sm text-amber-800 font-semibold flex items-center gap-2" data-testid="winback-low-credits">
                <AlertTriangle className="h-4 w-4 shrink-0" /> {a.winback_low_credits}
              </div>
            ) : null}

            <div>
              <Label className="text-xs flex items-center gap-1.5">
                {a.winback_message} · {"{name}"} = {lang === "el" ? "όνομα πελάτη" : "customer name"}
                {!isDev && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-brand-ink/50 normal-case" data-testid="winback-locked-hint">
                    <Lock className="h-3 w-3" /> {a.winback_locked}
                  </span>
                )}
              </Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                readOnly={!isDev}
                className={`rounded-xl border-brand/20 min-h-[100px] mt-1 ${isDev ? "bg-white" : "bg-stone-alt/50 text-brand-ink/70 cursor-not-allowed select-text"}`}
                data-testid="winback-message"
              />
            </div>

            <div className="rounded-xl bg-white border border-brand/15 p-3.5">
              <label className="flex items-center gap-2 text-sm font-semibold text-brand-ink cursor-pointer">
                <Switch checked={couponOn} onCheckedChange={setCouponOn} data-testid="winback-coupon-toggle" />
                <Ticket className="h-4 w-4 text-brand" /> {a.coupon_add}
              </label>
              {couponOn && (
                <div className="mt-3 grid grid-cols-2 gap-3" data-testid="winback-coupon-form">
                  <div>
                    <Label className="text-xs">{a.coupon_kind}</Label>
                    <Select value={coupon.kind} onValueChange={(v) => setCoupon({ ...coupon, kind: v })}>
                      <SelectTrigger className={inputCls} data-testid="coupon-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">{a.coupon_percent}</SelectItem>
                        <SelectItem value="euro">{a.coupon_euro}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{a.coupon_amount}</Label>
                    <Input type="number" min="1" max={coupon.kind === "percent" ? 100 : 100} value={coupon.amount} onChange={(e) => setCoupon({ ...coupon, amount: Number(e.target.value) })} className={inputCls} data-testid="coupon-amount" />
                  </div>
                  <div>
                    <Label className="text-xs">{a.coupon_expires}</Label>
                    <Input type="date" value={coupon.expires} onChange={(e) => setCoupon({ ...coupon, expires: e.target.value })} className={inputCls} data-testid="coupon-expires" />
                  </div>
                  <div>
                    <Label className="text-xs">{a.coupon_service}</Label>
                    <Select value={coupon.service} onValueChange={(v) => setCoupon({ ...coupon, service: v })}>
                      <SelectTrigger className={inputCls} data-testid="coupon-service"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">{a.coupon_both}</SelectItem>
                        <SelectItem value="selfwash">{a.coupon_selfwash}</SelectItem>
                        <SelectItem value="grooming">{a.coupon_grooming}</SelectItem>
                        <SelectItem value="boutique">Boutique</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="col-span-2 text-[11px] text-brand-ink/50">{a.coupon_note}</p>
                </div>
              )}
            </div>

            <div className="rounded-xl bg-stone-alt/60 border border-brand/10 px-3 py-2" data-testid="winback-preview">
              <p className="text-[10px] uppercase tracking-wider text-brand-ink/40 mb-1">
                {a.winback_preview_for.replace("{name}", previewName)}
              </p>
              <p className="text-sm text-brand-ink/80 whitespace-pre-wrap font-mono">{message.replace("{name}", previewName)}{previewCoupon}{channel === "sms" ? "\nCare For Me" : ""}</p>
              {selected.size > 1 && (
                <p className="mt-2 text-[11px] text-brand font-semibold flex items-center gap-1" data-testid="winback-personalized-note">
                  <MessageSquare className="h-3 w-3" /> {a.winback_personalized}
                </p>
              )}
            </div>

            <Button
              onClick={send}
              disabled={sending || !message.trim() || lowCredits}
              className="w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11"
              data-testid="winback-send-btn"
            >
              <Send className="h-4 w-4 mr-2" /> {sending ? "..." : `${a.winback_send} (${selected.size})`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
